import { z } from 'zod'
import { ShoppingPromotionSchema } from '@reward-platform/shared-schemas'
import {
  BasketItem,
  CarHireBasketItem,
  ExperienceBasketItem,
  HotelBasketItem,
  FlightBasketItem,
  PaymentOptions,
} from '../basket'
import {
  CarHireItemType,
  ExperienceItemType,
  FlightItemType,
  HotelItemType,
  optionalField,
} from '../common'

export type CreateSelectedPaymentOptionsSchemaParams = {
  hotelBasketItem?: HotelBasketItem
  carHireBasketItem?: CarHireBasketItem
  experienceBasketItem?: ExperienceBasketItem
  flightBasketItem?: FlightBasketItem

  // Defines whether the schema should allow an item that does not have payment option assigned.
  // For example in a scenario where a new item has been added the basket but the user hasn't
  // selected a payment option yet.
  allowItemWithoutPaymentOption?: boolean
}

const getPaymentOptionsIds = (paymentOptions: PaymentOptions | undefined) =>
  paymentOptions
    ? [
        ...(paymentOptions.onlyCashPaymentOption?.id
          ? [paymentOptions.onlyCashPaymentOption.id]
          : []),
        ...(paymentOptions.options.map(({ id }) => id) ?? []),
      ]
    : []

const createPaymentOptionIdSchema = (basketItem: BasketItem) => {
  const paymentOptionIds = getPaymentOptionsIds(basketItem.paymentOptions)
  const {
    paymentOptions: { onlyCashPaymentOption },
  } = basketItem

  const balanceBoosterCodes = onlyCashPaymentOption?.balanceBoosters?.map(({ code }) => code)
  const balanceBoosterCodeSchema = z
    .preprocess(
      (val) => {
        if (typeof val === 'string' && balanceBoosterCodes?.includes(val)) {
          return val
        }

        return null
      },
      balanceBoosterCodes?.length ? z.string() : z.null()
    )
    .nullable()
    .optional()

  return z
    .object({
      basketItemId: z.literal(basketItem.id),
      paymentOptionId: z.string(),
      selectedByDefault: z.boolean(),
      balanceBooster: balanceBoosterCodeSchema,
      promotions: z.array(ShoppingPromotionSchema).optional(),
    })
    .transform((val) => {
      if (val.paymentOptionId !== onlyCashPaymentOption?.id) {
        return {
          ...val,
          balanceBooster: undefined,
        }
      }
      return val
    })
    .refine(({ paymentOptionId }) => paymentOptionIds.includes(paymentOptionId), {
      message: `paymentOptionId must be one of: ${paymentOptionIds.join(',')}`,
    })
}

export const createSelectedPaymentOptionsSchema = ({
  hotelBasketItem,
  carHireBasketItem,
  experienceBasketItem,
  flightBasketItem,
  allowItemWithoutPaymentOption = false,
}: CreateSelectedPaymentOptionsSchemaParams) => {
  const createItemSchema = (basketItem: BasketItem | undefined) => {
    const schema = z.preprocess(
      (val) => {
        if (basketItem) {
          return val
        }

        return undefined
      },
      basketItem ? createPaymentOptionIdSchema(basketItem) : z.undefined()
    )

    return optionalField(allowItemWithoutPaymentOption ? schema.optional() : schema)
  }

  return z.object({
    [HotelItemType]: createItemSchema(hotelBasketItem),
    [CarHireItemType]: createItemSchema(carHireBasketItem),
    [ExperienceItemType]: createItemSchema(experienceBasketItem),
    [FlightItemType]: createItemSchema(flightBasketItem),
  })
}

export type SelectedPaymentOptions = z.infer<ReturnType<typeof createSelectedPaymentOptionsSchema>>
